import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ADMIN_VIEWS_FUNDS_QUERY_KEY } from '@/api/admin/queries';
import { _approveFund, _blockOrRejectFund, _deleteFund, _unblockFund } from '@/api/fund/mutations';
import { FUND_QUERY_KEYS } from '@/api/fund/queries';
import { FundApprovalStatus, fundApprovalStatus } from '@/api/fund/types';
import DeleteIcon from '@/assets/svg/ui-elements/delete-filled-red.svg';
import LockOutlineIcon from '@/assets/svg/ui-elements/lock-outline.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { getCommunicationContactPath } from '@/pages/communications-v2/utils/get-communication-contact-path';
import { PagePaths } from '@/routes/page-paths';

import { overviewTabKeys } from '../data';

const FundActionKey = {
  DELETE: 'delete',
  APPROVE: 'approve',
  REJECT: 'reject',
  RESTORE: 'restore',
  BLOCK: 'block',
  UNBLOCK: 'unblock',
  CONTACT: 'contact',
  EDIT: 'edit',
} as const;
type FundActionKeyType = (typeof FundActionKey)[keyof typeof FundActionKey];

export const FUND_ACTIONS_BY_STATUS: Record<FundApprovalStatus, FundActionKeyType[]> = {
  [fundApprovalStatus.Draft]: [FundActionKey.DELETE, FundActionKey.CONTACT, FundActionKey.EDIT],
  [fundApprovalStatus.AwaitingApproval]: [
    FundActionKey.APPROVE,
    FundActionKey.REJECT,
    FundActionKey.EDIT,
    FundActionKey.CONTACT,
    FundActionKey.DELETE,
  ],
  [fundApprovalStatus.Rejected]: [
    FundActionKey.RESTORE,
    FundActionKey.DELETE,
    FundActionKey.EDIT,
    FundActionKey.CONTACT,
  ],
  [fundApprovalStatus.Verified]: [FundActionKey.BLOCK, FundActionKey.EDIT, FundActionKey.CONTACT],
  [fundApprovalStatus.Blocked]: [
    FundActionKey.UNBLOCK,
    FundActionKey.DELETE,
    FundActionKey.EDIT,
    FundActionKey.CONTACT,
  ],
  [fundApprovalStatus.ReVerificationPending]: [
    FundActionKey.APPROVE,
    FundActionKey.REJECT,
    FundActionKey.EDIT,
    FundActionKey.CONTACT,
  ],
};

type FundLike = {
  id: string;
  name: string | null;
  entityId: string;
  fundApprovalStatus: FundApprovalStatus;
  entityApproved: boolean;
};

export const useFundAdminActions = (fund: FundLike) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const actions = FUND_ACTIONS_BY_STATUS[fund.fundApprovalStatus];

  const show = useMemo(() => {
    const set = new Set(actions);

    return Object.fromEntries(Object.values(FundActionKey).map((k) => [k, set.has(k)])) as Record<
      FundActionKeyType,
      boolean
    >;
  }, [actions]);

  const communicationContactPath = getCommunicationContactPath({
    recipientType: 'EntityDesk',
    recipientId: fund.entityId,
  });

  const onMutationSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ADMIN_VIEWS_FUNDS_QUERY_KEY.fundsRoot });
    await queryClient.invalidateQueries({ queryKey: [FUND_QUERY_KEYS.funds, fund.id] });
  };

  const approve = useMutation({
    mutationFn: _approveFund,
    onSuccess: onMutationSuccess,
  });

  const del = useMutation({
    mutationFn: _deleteFund,
    onSuccess: async () => {
      navigate(`${PagePaths.overview}?tab=${overviewTabKeys.Funds}`);
      await queryClient.invalidateQueries({ queryKey: ADMIN_VIEWS_FUNDS_QUERY_KEY.fundsRoot });
    },
  });
  const blockOrReject = useMutation({
    mutationFn: _blockOrRejectFund,
    onSuccess: onMutationSuccess,
  });
  const unblock = useMutation({ mutationFn: _unblockFund, onSuccess: onMutationSuccess });

  const { handleOpen: openDeleteModal, handleClose: closeDeleteModal } = useGlobalModal(
    'CONFIRM_ACTION_MODAL',
    {
      modalTitle: 'Delete Fund',
      btnLabel: 'Delete',
      confirmationTitle: 'Are you sure you want to Delete this Fund?',
      confirmationDescription: 'This action cannot be reversed. All fund data will be lost.',
      icon: <DeleteIcon />,
      isLoading: del.isPending,
      onConfirm: async () => {
        await del.mutateAsync(fund.id);
        closeDeleteModal();
      },
      btnColor: 'red',
    },
  );

  const { handleOpen: openRejectModal, handleClose: closeRejectModal } = useGlobalModal(
    'REJECT_ACTION_MODAL',
    {
      type: 'fund',
      onConfirm: async (reasons) => {
        await blockOrReject.mutateAsync({ fundId: fund.id, ...reasons });
        closeRejectModal();
      },
      onContact: communicationContactPath,
      isLoading: blockOrReject.isPending,
    },
  );

  const { handleOpen: openRestoreModal, handleClose: closeRestoreModal } = useGlobalModal(
    'RESTORE_ACTION_MODAL',
    {
      type: 'fund',
      onConfirm: async () => {
        await unblock.mutateAsync(fund.id);
        closeRestoreModal();
      },
      isLoading: unblock.isPending,
    },
  );

  const { handleOpen: openBlockModal, handleClose: closeBlockModal } = useGlobalModal(
    'BLOCK_ACTION_MODAL',
    {
      type: 'fund',
      isLoading: blockOrReject.isPending,
      onConfirm: async (reasons) => {
        await blockOrReject.mutateAsync({ fundId: fund.id, ...reasons });
        closeBlockModal();
      },
    },
  );

  const { handleOpen: openUnblockModal, handleClose: closeUnblockModal } = useGlobalModal(
    'CONFIRM_ACTION_MODAL',
    {
      modalTitle: 'Unblock Fund',
      btnLabel: 'Unblock',
      confirmationTitle: 'Are you sure you want to unblock this Fund?',
      icon: <LockOutlineIcon width={24} height={24} color="#39CA88" />,
      isLoading: unblock.isPending,
      onConfirm: async () => {
        await unblock.mutateAsync(fund.id);
        closeUnblockModal();
      },
      btnColor: 'orange',
    },
  );

  const contact = () => {
    navigate(communicationContactPath);
  };

  return {
    show,
    handlers: {
      approve: () => approve.mutate(fund.id),
      openDeleteModal,
      openRejectModal,
      openRestoreModal,
      openBlockModal,
      openUnblockModal,
      contact,
    },
  };
};
